import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import applicantApi from "@/lib/applicantApi";
import { useApplicantAuth } from "@/context/ApplicantAuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Play,
  ArrowLeft,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Sparkles,
} from "lucide-react";

// -----------------------------------------------------------------------------
// YouTube IFrame API loader (singleton)
// -----------------------------------------------------------------------------
let _ytApiPromise = null;
const loadYouTubeApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (_ytApiPromise) return _ytApiPromise;
  _ytApiPromise = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(s);
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
  });
  return _ytApiPromise;
};

const TrainingVideo = () => {
  const { module_id } = useParams();
  const nav = useNavigate();
  const { applicant, checking } = useApplicantAuth();

  const [module, setModule] = useState(null);
  const [progress, setProgress] = useState({ max_watched_seconds: 0, last_position_seconds: 0, completed_at: null, passed: false, score_pct: 0 });
  const [error, setError] = useState("");

  // Video state
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const lastSavedRef = useRef(0);
  const [videoWatchedEnough, setVideoWatchedEnough] = useState(false);

  // Assessment state
  const [showAssessment, setShowAssessment] = useState(false);
  const [answers, setAnswers] = useState({}); // { qid: answered_index }
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {score_pct, passed, correct, total, passing_score}

  // -----------------------------------------------------------------------
  // Load module + prior progress
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (checking) return;
    if (!applicant) { nav("/careers/login"); return; }
    (async () => {
      try {
        const [{ data: modules }, { data: prog }] = await Promise.all([
          applicantApi.get("/applicants/me/training-modules"),
          applicantApi.get(`/applicants/me/training-progress/${module_id}`),
        ]);
        const m = modules.find((x) => x.id === module_id);
        if (!m) { setError("This training module is not assigned to your role."); return; }
        setModule(m);
        setProgress(prog || {});
        if (prog?.max_watched_seconds >= (m.duration_seconds || 0) * 0.9) setVideoWatchedEnough(true);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load training.");
      }
    })();
  }, [module_id, applicant, checking, nav]);

  // -----------------------------------------------------------------------
  // Initialize YouTube player once module is loaded
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!module?.youtube_id || !containerRef.current) return;
    let destroyed = false;
    let tickInterval;

    (async () => {
      const YT = await loadYouTubeApi();
      if (destroyed) return;
      playerRef.current = new YT.Player(containerRef.current, {
        videoId: module.youtube_id,
        playerVars: {
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: (e) => {
            setDuration(e.target.getDuration() || module.duration_seconds || 0);
            // Resume where left off
            const resume = Math.min(progress.last_position_seconds || 0, (progress.max_watched_seconds || 0));
            if (resume > 3) e.target.seekTo(resume, true);
          },
          onStateChange: (e) => {
            // PLAYING = 1, PAUSED = 2, ENDED = 0
            if (e.data === 0) {
              // ended → allow taking assessment
              setVideoWatchedEnough(true);
              flushSave(true);
            }
          },
        },
      });

      tickInterval = setInterval(() => {
        const p = playerRef.current;
        if (!p || typeof p.getCurrentTime !== "function") return;
        const t = p.getCurrentTime() || 0;
        setCurrentTime(t);
        setProgress((prev) => {
          const nextMax = Math.max(prev.max_watched_seconds || 0, t);
          return { ...prev, max_watched_seconds: nextMax, last_position_seconds: t };
        });
        if (duration && t / duration >= 0.9) setVideoWatchedEnough(true);
        // throttle server save every 8s
        if (t - lastSavedRef.current > 8) {
          lastSavedRef.current = t;
          applicantApi.patch(`/applicants/me/training-progress/${module_id}`, {
            max_watched_seconds: t,
            last_position_seconds: t,
          }).catch(() => {});
        }
      }, 1000);
    })();

    return () => {
      destroyed = true;
      if (tickInterval) clearInterval(tickInterval);
      try { playerRef.current?.destroy(); } catch (_) { /* noop */ }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module?.youtube_id]);

  const flushSave = useCallback(async (markComplete = false) => {
    const t = playerRef.current?.getCurrentTime?.() || currentTime || 0;
    try {
      await applicantApi.patch(`/applicants/me/training-progress/${module_id}`, {
        max_watched_seconds: markComplete ? Math.max(duration, t) : t,
        last_position_seconds: t,
      });
    } catch (_) { /* ignore */ }
  }, [currentTime, duration, module_id]);

  // -----------------------------------------------------------------------
  // Assessment submission
  // -----------------------------------------------------------------------
  const submitAssessment = async () => {
    if (!module?.final_questions?.length) return;
    const missing = module.final_questions.filter((q) => answers[q.id] === undefined);
    if (missing.length) {
      toast.error(`Please answer all ${module.final_questions.length} questions before submitting.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { answers: module.final_questions.map((q) => ({ qid: q.id, answered_index: answers[q.id] })) };
      const { data } = await applicantApi.post(`/applicants/me/training-progress/${module_id}/assessment`, payload);
      setResult(data);
      if (data.passed) {
        toast.success(`Passed with ${data.score_pct}%`);
      } else {
        toast.error(`Score: ${data.score_pct}% — you need ${data.passing_score}% to pass. Please review and retake.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit assessment.");
    } finally {
      setSubmitting(false);
    }
  };

  const retakeAssessment = () => {
    setAnswers({});
    setResult(null);
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  if (checking) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
      <XCircle size={40} className="text-primary mb-3" />
      <div className="font-serif-display text-2xl">{error}</div>
      <Link to="/careers/dashboard" className="mt-6 text-primary text-sm">Back to dashboard</Link>
    </div>
  );
  if (!module) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading module…</div>;

  const watchedPct = duration ? Math.min(100, Math.round(((progress.max_watched_seconds || currentTime) / duration) * 100)) : 0;
  // Filter out integrity questions from the visible count (they still get submitted, but hidden from applicant framing).
  // Actually — we DO show all questions to the applicant (they never know which are integrity). So we show every final_question.
  const questions = module.final_questions || [];
  const nonIntegrityCount = questions.filter((q) => q.type !== "integrity").length;

  return (
    <main data-testid="training-video-page" className="min-h-screen bg-background">
      <div className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/careers/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft size={14} /> Dashboard
          </Link>
          <Badge className="rounded-full bg-primary/10 text-primary" data-testid="training-security-badge">
            <ShieldCheck size={12} className="mr-1" /> Watch fully · then complete the assessment
          </Badge>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-8">
        <div className="text-xs tracking-[0.25em] uppercase text-primary/80">Training · {module.category?.replace(/_/g, " ")}</div>
        <h1 className="mt-3 font-serif-display text-3xl md:text-4xl">{module.title}</h1>
        {module.description && (
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-2xl">{module.description}</p>
        )}

        {/* YouTube Player */}
        <div className="mt-8 relative rounded-2xl overflow-hidden bg-black border border-border aspect-video">
          <div ref={containerRef} data-testid="training-youtube-container" className="absolute inset-0 w-full h-full" />
        </div>

        {/* Progress card */}
        <div className="mt-6 rounded-2xl border border-border/70 bg-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Video progress</div>
              <div className="font-serif-display text-xl mt-1" data-testid="watch-progress-label">
                {progress.passed ? "Passed & verified" : videoWatchedEnough ? "Video watched — ready for assessment" : `${watchedPct}% watched`}
              </div>
            </div>
            {progress.passed && <CheckCircle2 size={26} className="text-primary" />}
          </div>
          <Progress value={watchedPct} className="mt-4 h-2" />
        </div>

        {/* Take assessment button */}
        {!showAssessment && !result && (
          <div className="mt-8 rounded-2xl border border-primary/30 bg-primary/5 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <Sparkles size={20} className="text-primary mt-0.5" />
              <div>
                <div className="font-serif-display text-xl">Final assessment</div>
                <div className="text-sm text-muted-foreground mt-1">
                  {questions.length} questions · you need <strong>{module.passing_score}%</strong> to pass ({nonIntegrityCount} counted; the rest are situational judgment items).
                </div>
              </div>
            </div>
            <Button
              data-testid="btn-open-assessment"
              disabled={!videoWatchedEnough}
              onClick={() => setShowAssessment(true)}
              className="rounded-full bg-primary text-primary-foreground h-11 px-6 disabled:opacity-40"
            >
              {videoWatchedEnough ? "Take assessment" : "Watch the video first"}
            </Button>
          </div>
        )}

        {/* Assessment */}
        {showAssessment && !result && (
          <div data-testid="assessment-panel" className="mt-8 space-y-6">
            <div>
              <div className="text-xs tracking-[0.25em] uppercase text-primary/80">Assessment</div>
              <h2 className="mt-2 font-serif-display text-2xl">Answer honestly — there are no trick questions.</h2>
              <p className="mt-2 text-sm text-muted-foreground">Your responses guide how we place you. Take your time.</p>
            </div>
            {questions.map((q, i) => (
              <div key={q.id} data-testid={`question-${q.id}`} className="rounded-2xl border border-border/70 bg-card p-6">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Question {i + 1} of {questions.length}</div>
                <div className="mt-2 text-base md:text-lg text-foreground font-medium leading-relaxed">{q.prompt}</div>
                <div className="mt-4 space-y-2">
                  {q.options.map((opt, idx) => {
                    const selected = answers[q.id] === idx;
                    return (
                      <button
                        key={idx}
                        type="button"
                        data-testid={`opt-${q.id}-${idx}`}
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: idx }))}
                        className={`w-full text-left p-4 rounded-xl border transition ${
                          selected
                            ? "border-primary bg-primary/10 text-foreground"
                            : "border-border hover:border-primary/40 hover:bg-primary/5"
                        }`}
                      >
                        <div className="text-sm leading-relaxed">{opt}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <Button data-testid="btn-submit-assessment" onClick={submitAssessment} disabled={submitting} className="rounded-full h-12 px-7 bg-primary text-primary-foreground">
                {submitting ? "Submitting…" : "Submit assessment"}
              </Button>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div data-testid="assessment-result" className={`mt-8 rounded-2xl border p-6 ${result.passed ? "border-primary/40 bg-primary/5" : "border-destructive/40 bg-destructive/5"}`}>
            {result.passed ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 size={22} className="text-primary mt-0.5" />
                <div>
                  <div className="font-serif-display text-2xl">Passed — {result.score_pct}%</div>
                  <div className="text-sm text-muted-foreground mt-1">You got {result.correct} of {result.total} graded questions correct. This module is now complete on your record.</div>
                  <Button asChild data-testid="btn-next-module" className="mt-4 rounded-full bg-primary text-primary-foreground">
                    <Link to="/careers/dashboard">Back to dashboard</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <XCircle size={22} className="text-destructive mt-0.5" />
                <div>
                  <div className="font-serif-display text-2xl">Score: {result.score_pct}%</div>
                  <div className="text-sm text-muted-foreground mt-1">You need {result.passing_score}% to pass. Please review the video and try again.</div>
                  <div className="mt-4 flex gap-3">
                    <Button data-testid="btn-retake-assessment" onClick={retakeAssessment} className="rounded-full bg-primary text-primary-foreground">Retake assessment</Button>
                    <Button asChild data-testid="btn-back-to-dashboard" variant="outline" className="rounded-full">
                      <Link to="/careers/dashboard">Back to dashboard</Link>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

export default TrainingVideo;
